import styled from 'styled-components';

export type AskHubThemeMode = 'default' | 'compact';

export const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

export const ContentSectionContainer = styled.div`
  margin: 0 auto;
  padding: 2rem;
`;

export const ContentSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

export const ContentSectionLists = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
`;

export const Title = styled.h1`
  margin: 0 0 16px 0;
  font-size: 28px;
  font-weight: 600;
  color: #1f2937;
`;

export const WorkspaceId = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: #6b7280;
  font-family: monospace;
`;

export const PlaceholderText = styled.p`
  color: #6b7280;
  font-size: 16px;
`;

export const CardGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  flex-direction: row;
  gap: 1rem;
`;

export const Icon = styled.div`
  font-size: 24px;
  margin-bottom: 8px;
  color: #007bff;
`;

export const Description = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

export const Count = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

export const Update = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

// ============= Ask Session Item Styles =============

export const AskSessionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const AskSessionItem = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #0c8;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
  }
`;

export const ThemedAskSessionItem = styled(AskSessionItem)<{
  $themeMode: AskHubThemeMode;
}>`
  ${({ $themeMode }) =>
    $themeMode === 'compact' &&
    `
      border-radius: 14px;
      border: 1px solid #ececf1;
      box-shadow: none;
      background: #ffffff;
      padding: 1rem 1.1rem;

      &:hover {
        border-color: #d9d9e3;
        box-shadow: 0 2px 10px rgba(17, 24, 39, 0.06);
      }
    `}
`;

export const AskSessionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

export const AskSessionTitle = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
`;

export const AskSessionDate = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  white-space: nowrap;
  margin-left: 1rem;
`;

export const AskSessionMeta = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f3f4f6;
`;

export const ThemedAskSessionMeta = styled(AskSessionMeta)<{
  $themeMode: AskHubThemeMode;
}>`
  ${({ $themeMode }) =>
    $themeMode === 'compact' &&
    `
      gap: 1rem;
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      border-top: 1px solid #ececf1;

      @media (max-width: 1100px) {
        grid-template-columns: 1fr;
      }
    `}
`;

export const AskSessionMetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const AskSessionMetaLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const AskSessionMetaValue = styled.span`
  font-size: 0.875rem;
  color: #1f2937;
  font-weight: 500;
`;

export const CreatorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
`;

export const EmptyStateText = styled.p`
  margin: 0.5rem 0;
  font-size: 1rem;
`;

export const ContentSectionWithPadding = styled(ContentSection)`
  padding: 1.5rem;
`;

export const HeaderRow = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ResultsCount = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.5rem;
`;

export const ThemeToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 0.75rem;
`;

export const ThemeToggle = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem;
  border-radius: 999px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
`;

export const ThemeToggleButton = styled.button<{ $active: boolean }>`
  border: none;
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  color: ${({ $active }) => ($active ? '#111827' : '#6b7280')};
  background: ${({ $active }) => ($active ? 'white' : 'transparent')};
  box-shadow: ${({ $active }) =>
    $active ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'};
  transition: all 0.2s ease;
`;

export const AskHubContentBody = styled.div<{ $themeMode: AskHubThemeMode }>`
  ${({ $themeMode }) =>
    $themeMode === 'compact' &&
    `
      padding: 1.5rem;
      border-radius: 12px;
      background: #f7f7f8;
      border: 1px solid #ececf1;
    `}
`;

export const EditableTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`;

export const EditableTitleInput = styled.input`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  border: 1px solid #3b82f6;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  background: white;
  flex: 1;
  outline: none;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

export const SaveButton = styled.button`
  background: #10b981;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: #059669;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

export const CancelButton = styled.button`
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: #4b5563;
  }
`;

export const EditButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  margin-left: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;

  ${AskSessionItem}:hover & {
    opacity: 1;
  }

  &:hover {
    color: #1f2937;
  }
`;

export const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

// Delete button (shown on hover, like Edit)
export const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  margin-left: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;

  ${AskSessionItem}:hover & {
    opacity: 1;
  }

  &:hover {
    color: #dc2626;
  }
`;

// Delete confirmation modal
export const DeleteModalContent = styled.div`
  padding: 0.25rem 0;
  min-width: 320px;
`;

export const DeleteModalTitle = styled.p`
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
`;

export const DeleteModalDetails = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

export const DeleteModalDetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const DeleteModalDetailLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const DeleteModalDetailValue = styled.span`
  font-size: 0.875rem;
  color: #1f2937;
`;

export const DeleteModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;
